// Hardcoded concept data for the CAT-993 docs nav prototype.
// These are intentionally seeded from the live editorial catalog
// (USA + EST as of 2026-05-05) so the prototype reflects real shape.

import type { Signal } from "./methodology"

export interface LocationType {
  id: string
  name: string
  description: string
  availableMetrics: string[] // metric ids
  exampleUseCases: string[]
}

export interface Metric {
  id: string
  name: string
  locationType: string
  questionType: "what+when" | "who" | "how" | "where"
  description: string
  inputs: string[]
  cadence: string[]
  columns: { name: string; type: string; description: string }[]
  strengths: string[]
  limitations: string[]
}

export interface MetricModel {
  id: string
  ltm: string // metric id
  country: string
  methodology: string
  supply: string // supply id
  algorithm: string
  perCountryLimitations: string[]
  coverageCaveats: string
}

export interface LocationSet {
  id: string
  name: string
  /** Name with country context stripped, used inside the country catalog. */
  shortName: string
  locationType: string
  country: string
  source: string
  description: string
  coverage: string
}

export interface SupplySource {
  id: string
  name: string
  /** Name with country context stripped, used inside the country catalog. */
  shortName: string
  country: string
  /**
   * The kind of signal this source carries. Determines which methodology
   * entries apply in the countries this source serves — see methodology.ts.
   */
  signal: Signal
  description: string
  /** Optional markdown body rendered below the description on the detail page. */
  body?: string
  availability?: string
  latestData?: string
}

export const locationTypes: LocationType[] = [
  {
    id: "place",
    name: "Place",
    description:
      "A point of interest: a building, a store, a venue. Place datasets describe the activity *at* a specific physical location.",
    availableMetrics: [
      "foot-traffic",
      "visit-length",
      "popular-times",
      "visitor-profile",
      "visitor-demographics",
      "visitor-journey",
      "trade-area",
    ],
    exampleUseCases: [
      "Site selection: compare candidate sites by visit volume",
      "Brand analysis: track performance across a portfolio",
      "Competitive intelligence: benchmark against competitors at the same locations",
    ],
  },
  {
    id: "area",
    name: "Area",
    description:
      "A bounded geographical region: a neighborhood, a region, a grid cell, a ZIP code. Area datasets describe activity *within* an area, useful when point-of-interest granularity is too narrow.",
    availableMetrics: [
      "foot-traffic-vwr",
      "visit-length-area",
      "visitor-profile-area",
      "visitor-demographics-area",
      "origin-destination",
      "trade-area-area",
    ],
    exampleUseCases: [
      "Reporting of area development: track foot traffic at the neighborhood level",
      "Public infrastructure planning: identify high-density zones",
      "Real-estate due diligence: understand local visitor flows",
    ],
  },
  {
    id: "way",
    name: "Way",
    description:
      "A linear feature like a road segment or pedestrian path. Way datasets describe traffic *along* a route.",
    availableMetrics: ["traffic", "origin-destination-way"],
    exampleUseCases: [
      "Transportation modeling: estimate daily traffic on a road segment",
      "Infrastructure planning: identify chronically congested links",
    ],
  },
]

export const metrics: Metric[] = [
  {
    id: "foot-traffic",
    name: "Foot Traffic",
    locationType: "place",
    questionType: "what+when",
    description:
      "Aggregated visit counts to a place over a time period. Foot traffic is the most widely used Pine59 metric and the foundation for site comparison, trend analysis, and competitive benchmarks.",
    inputs: ["panel-based GPS observations", "place geometry"],
    cadence: ["Daily", "Weekly", "Monthly", "Annual"],
    columns: [
      {
        name: "location_id",
        type: "STRING",
        description: "The unique identifier of the place.",
      },
      {
        name: "observation_start_date",
        type: "DATE",
        description: "Start of the period the row covers.",
      },
      {
        name: "observation_end_date",
        type: "DATE",
        description: "End of the period the row covers (exclusive).",
      },
      {
        name: "visits_sum",
        type: "INTEGER",
        description: "Modeled total visit-events in the period.",
      },
      {
        name: "visits_p50",
        type: "FLOAT",
        description: "Median modeled visit-events per location.",
      },
    ],
    strengths: [
      "Uniform methodology across countries makes cross-market benchmarking possible.",
      "Daily and weekly cadences capture weekday and week-over-week patterns.",
    ],
    limitations: [
      "Visit counts are modeled estimates of visit-events, not unique persons.",
      "Sparse panels in some markets reduce confidence at long-tail locations.",
    ],
  },
  {
    id: "visit-length",
    name: "Visit Length",
    locationType: "place",
    questionType: "what+when",
    description:
      "Distribution of visit durations at a place. Useful for distinguishing dwell-heavy venues (cafés, gyms) from quick-stop venues (gas stations, convenience).",
    inputs: ["panel-based GPS observations", "place geometry"],
    cadence: ["Monthly"],
    columns: [
      { name: "location_id", type: "STRING", description: "" },
      { name: "median_dwell_minutes", type: "FLOAT", description: "Median dwell time in minutes." },
      { name: "p25_dwell_minutes", type: "FLOAT", description: "" },
      { name: "p75_dwell_minutes", type: "FLOAT", description: "" },
    ],
    strengths: ["Pairs naturally with foot traffic to differentiate location types."],
    limitations: ["Short visits below the panel sampling rate may be undercounted."],
  },
  {
    id: "popular-times",
    name: "Popular Times",
    locationType: "place",
    questionType: "what+when",
    description:
      "Hour-of-day and day-of-week visitation patterns at a place. Reveals peak hours, off-peak windows, and weekday/weekend differences.",
    inputs: ["panel-based GPS observations"],
    cadence: ["Hourly aggregated to weekly profile"],
    columns: [
      { name: "location_id", type: "STRING", description: "" },
      { name: "day_of_week", type: "INTEGER", description: "" },
      { name: "hour_of_day", type: "INTEGER", description: "" },
      { name: "relative_visits", type: "FLOAT", description: "Visit share for this hour normalized within the location." },
    ],
    strengths: ["Captures behavioral patterns invisible in daily aggregates."],
    limitations: ["Requires sufficient observations per hour bucket; sparse markets may have noisy estimates."],
  },
  {
    id: "visitor-profile",
    name: "Visitor Profile",
    locationType: "place",
    questionType: "who",
    description:
      "Aggregated profile of visitors to a place: home regions, demographics, behavioral cohorts. Useful for understanding catchment areas and audience composition.",
    inputs: ["panel observations linked to home-area inferences"],
    cadence: ["Monthly"],
    columns: [
      { name: "location_id", type: "STRING", description: "" },
      { name: "home_region", type: "STRING", description: "" },
      { name: "share_of_visits", type: "FLOAT", description: "Fraction of visits originating from home_region." },
    ],
    strengths: ["Identifies trade-area composition and audience reach."],
    limitations: ["Privacy aggregation thresholds suppress small cohorts."],
  },
  {
    id: "visitor-demographics",
    name: "Visitor Demographics",
    locationType: "place",
    questionType: "who",
    description:
      "Demographic breakdown of visitors derived from census-linked home areas. Returns fractional shares (0.0–1.0), not absolute counts.",
    inputs: ["panel observations", "census-linked demographics"],
    cadence: ["Monthly"],
    columns: [
      { name: "location_id", type: "STRING", description: "" },
      { name: "pct_18_24", type: "FLOAT", description: "" },
      { name: "pct_25_44", type: "FLOAT", description: "" },
      { name: "pct_income_high", type: "FLOAT", description: "" },
    ],
    strengths: ["Lets you build audience profiles per location at scale."],
    limitations: ["Modeled, not surveyed, so it works best when averaged across many locations."],
  },
  {
    id: "visitor-journey",
    name: "Visitor Journey",
    locationType: "place",
    questionType: "how",
    description:
      "Sequences of places visited before and after a target place. Reveals cross-shopping, shared catchments, and complementary venues.",
    inputs: ["panel trajectories"],
    cadence: ["Monthly"],
    columns: [
      { name: "location_id", type: "STRING", description: "" },
      { name: "co_visited_location_id", type: "STRING", description: "" },
      { name: "share_of_journeys", type: "FLOAT", description: "" },
    ],
    strengths: ["Surfaces non-obvious adjacency relationships."],
    limitations: ["Requires sufficient repeat visitation to be statistically meaningful."],
  },
  {
    id: "trade-area",
    name: "Trade Area",
    locationType: "place",
    questionType: "how",
    description:
      "The geographic area from which a place draws its visitors. Useful for catchment sizing and competitive overlap analysis.",
    inputs: ["panel observations linked to home-area inferences"],
    cadence: ["Monthly"],
    columns: [
      { name: "location_id", type: "STRING", description: "" },
      {
        name: "origin_id",
        type: "STRING",
        description: "Home-area identifier (e.g. census block group or postal area).",
      },
      {
        name: "capture_rate",
        type: "FLOAT",
        description: "Share of the place's visitors whose home is in this origin area.",
      },
    ],
    strengths: ["Quantifies pull strength geographically."],
    limitations: ["Edge effects near borders / coasts."],
  },
  {
    id: "foot-traffic-vwr",
    name: "Foot Traffic w/ Visitor, Worker & Resident",
    locationType: "area",
    questionType: "what+when",
    description:
      "Foot-traffic at the area level split by visitor / worker / resident roles. Lets you separate transient activity from baseline residential activity.",
    inputs: ["panel observations", "area geometry"],
    cadence: ["Daily", "Hourly", "Monthly"],
    columns: [
      { name: "location_id", type: "STRING", description: "" },
      { name: "visitors_sum", type: "INTEGER", description: "" },
      { name: "workers_sum", type: "INTEGER", description: "" },
      { name: "residents_sum", type: "INTEGER", description: "" },
    ],
    strengths: ["Critical for distinguishing destination areas from bedroom communities."],
    limitations: ["Worker/resident classification depends on inferred home/work areas."],
  },
  {
    id: "visit-length-area",
    name: "Visit Length",
    locationType: "area",
    questionType: "what+when",
    description: "Distribution of visit durations within an area.",
    inputs: ["panel observations", "area geometry"],
    cadence: ["Monthly"],
    columns: [
      { name: "location_id", type: "STRING", description: "" },
      { name: "median_dwell_minutes", type: "FLOAT", description: "" },
    ],
    strengths: ["Captures whether an area is for transit or dwell."],
    limitations: ["Aggregate over the area, so it does not localize to specific places inside it."],
  },
  {
    id: "visitor-profile-area",
    name: "Visitor Profile",
    locationType: "area",
    questionType: "who",
    description: "Aggregated profile of visitors entering an area.",
    inputs: ["panel observations linked to home-area inferences"],
    cadence: ["Monthly"],
    columns: [
      { name: "location_id", type: "STRING", description: "" },
      { name: "home_region", type: "STRING", description: "" },
      { name: "share_of_visits", type: "FLOAT", description: "" },
    ],
    strengths: ["Useful for area-level catchment analysis."],
    limitations: ["Privacy thresholds suppress small cohorts."],
  },
  {
    id: "visitor-demographics-area",
    name: "Visitor Demographics",
    locationType: "area",
    questionType: "who",
    description: "Demographic breakdown of visitors to an area.",
    inputs: ["panel observations", "census-linked demographics"],
    cadence: ["Monthly"],
    columns: [
      { name: "location_id", type: "STRING", description: "" },
      { name: "pct_18_24", type: "FLOAT", description: "" },
      { name: "pct_high_income", type: "FLOAT", description: "" },
    ],
    strengths: ["Pairs naturally with VWR to characterize area visitors."],
    limitations: ["Fractional values, not absolute counts."],
  },
  {
    id: "origin-destination",
    name: "Origin-Destination Matrix",
    locationType: "area",
    questionType: "how",
    description:
      "Flows of visitors between origin and destination areas over a time window. Foundational for transportation modeling and spatial-economy analysis. Currently Nordics-only.",
    inputs: ["panel trajectories", "area geometry"],
    cadence: ["Monthly"],
    columns: [
      { name: "origin_id", type: "STRING", description: "" },
      { name: "destination_id", type: "STRING", description: "" },
      { name: "trips", type: "INTEGER", description: "" },
    ],
    strengths: ["Direct input for trip-distribution and modal-split modeling."],
    limitations: ["Granularity bounded by the underlying area system."],
  },
  {
    id: "trade-area-area",
    name: "Trade Area",
    locationType: "area",
    questionType: "how",
    description: "The geographic area that contributes the most visitors to a target area.",
    inputs: ["panel observations linked to home-area inferences"],
    cadence: ["Monthly"],
    columns: [
      { name: "location_id", type: "STRING", description: "" },
      {
        name: "origin_id",
        type: "STRING",
        description: "Home-area identifier, in the origin area system.",
      },
      {
        name: "capture_rate",
        type: "FLOAT",
        description: "Share of the area's visitors whose home is in this origin area.",
      },
    ],
    strengths: ["Useful for retail and real-estate decisions at neighborhood scale."],
    limitations: ["Origin-area granularity bounds how sharply the catchment resolves."],
  },
  {
    id: "traffic",
    name: "Traffic",
    locationType: "way",
    questionType: "what+when",
    description:
      "Aggregated traffic counts on road segments. Segments come from OpenStreetMap (OSM), so every count is keyed to a public, stable way id you can join against your own network data. Coverage is the classified through-road network, from motorway down to tertiary. Currently Nordics-only.",
    inputs: ["panel trajectories", "OpenStreetMap way geometry"],
    cadence: ["Daily", "Monthly"],
    columns: [
      {
        name: "way_id",
        type: "STRING",
        description: "OpenStreetMap way identifier for the road segment.",
      },
      { name: "trips", type: "INTEGER", description: "" },
    ],
    strengths: [
      "Direct alternative to fixed traffic counters with much wider coverage.",
      "Keyed to OpenStreetMap way ids, so counts join directly to any OSM-based network model.",
    ],
    limitations: [
      "Sample-derived; absolute calibration depends on assumed expansion factors.",
      "Only the classified through-road network is covered (motorway down to tertiary, plus link roads). Residential and local streets, service roads, and footpaths are not included.",
    ],
  },
  {
    id: "origin-destination-way",
    name: "Origin-Destination Matrix (Way)",
    locationType: "way",
    questionType: "how",
    description: "Flows along way segments. Currently Nordics-only.",
    inputs: ["panel trajectories", "way geometry"],
    cadence: ["Monthly"],
    columns: [
      { name: "origin_way_id", type: "STRING", description: "" },
      { name: "destination_way_id", type: "STRING", description: "" },
      { name: "trips", type: "INTEGER", description: "" },
    ],
    strengths: ["Captures route choice along the network."],
    limitations: ["Sparse on minor roads."],
  },
]

export const metricModels: MetricModel[] = [
  {
    id: "foot-traffic-area-est-day",
    ltm: "foot-traffic-vwr",
    country: "EST",
    methodology:
      "Telco-derived signal with GPS calibration and area aggregation.",
    supply: "telia-est",
    algorithm:
      "Network-event observations are mapped to areas via base-station geometry, then refined with a GPS-based correction layer.",
    perCountryLimitations: [
      "Aggregation thresholds suppress areas with low visitor counts.",
    ],
    coverageCaveats: "Available for neighborhoods (al3), regions (al2), grid 250m, and Overture place sets.",
  },
  {
    id: "foot-traffic-area-est-hour",
    ltm: "foot-traffic-vwr",
    country: "EST",
    methodology: "Hourly granularity.",
    supply: "telia-est",
    algorithm: "Hourly aggregation of the daily model.",
    perCountryLimitations: [
      "Hourly buckets with very low counts are suppressed for privacy.",
    ],
    coverageCaveats: "Hourly listings cover Overture and grid 250m sets in Estonia.",
  },
  {
    id: "foot-traffic-area-est-month",
    ltm: "foot-traffic-vwr",
    country: "EST",
    methodology: "Monthly aggregation.",
    supply: "telia-est",
    algorithm: "Monthly aggregation of the daily model.",
    perCountryLimitations: [],
    coverageCaveats: "Available across all Estonian area location sets.",
  },
  {
    id: "visit-length-area-est-month",
    ltm: "visit-length-area",
    country: "EST",
    methodology: "Visit length distribution from panel observations.",
    supply: "telia-est",
    algorithm: "Dwell-time computation per visit-event, aggregated to medians/percentiles per area.",
    perCountryLimitations: [
      "Areas with sparse activity have noisier dwell estimates.",
    ],
    coverageCaveats: "Available for Overture place set in Estonia.",
  },

  // Norway
  {
    id: "foot-traffic-area-nor",
    ltm: "foot-traffic-vwr",
    country: "NOR",
    methodology:
      "Telia network events mapped to areas via base-station geometry, then refined with a panel-based correction layer.",
    supply: "telia-nor",
    algorithm:
      "Network observations are projected to areas, deduplicated per user-day, and split into visitor / worker / resident roles based on inferred home/work areas.",
    perCountryLimitations: [
      "Aggregation thresholds suppress areas with low visitor counts.",
      "Coverage is denser in urban areas; remote regions have wider confidence bands.",
    ],
    coverageCaveats:
      "Daily, hourly, and monthly cadences across basic statistical units (al4), statistical tracts (al3), municipalities (al2), 250m grid, and Overture places.",
  },
  {
    id: "visit-length-area-nor",
    ltm: "visit-length-area",
    country: "NOR",
    methodology: "Dwell-time distribution from Telia network events.",
    supply: "telia-nor",
    algorithm:
      "Dwell-time computation per visit-event, aggregated to medians/percentiles per area.",
    perCountryLimitations: [
      "Areas with sparse activity have noisier dwell estimates.",
    ],
    coverageCaveats: "Daily and monthly cadences across all Norwegian area location sets.",
  },
  {
    id: "visitor-demographics-area-nor",
    ltm: "visitor-demographics-area",
    country: "NOR",
    methodology:
      "Demographic profile of visitors, derived from carrier-side demography linked to anonymized panel cohorts.",
    supply: "telia-nor",
    algorithm:
      "Per-area cohort breakdown across age/gender bands; published as fractions, subject to K5 minimum-cohort suppression.",
    perCountryLimitations: [
      "Fractional values, not absolute counts.",
      "Demographic bands are coarse to preserve privacy.",
    ],
    coverageCaveats:
      "Monthly cadence across statistical tracts (al3), basic statistical units (al4), and Overture places. `resident_demographics_month` available for AL3/AL4 sets.",
  },
  {
    id: "trade-area-area-nor",
    ltm: "trade-area-area",
    country: "NOR",
    methodology: "Trade-area inference from home-area-linked panel observations.",
    supply: "telia-nor",
    algorithm:
      "Origins of observed visitors aggregated to the home area system; published as capture-rate per origin.",
    perCountryLimitations: [
      "Smoothing parameters affect boundary stability.",
      "Edge effects near borders / coasts.",
    ],
    coverageCaveats:
      "Daily and monthly cadences across municipalities, statistical tracts (al3), and basic statistical units (al4). Cross-system trade areas for Overture places are joined to AL3/AL4 origins.",
  },
  {
    id: "visitor-journey-nor",
    ltm: "visitor-journey",
    country: "NOR",
    methodology:
      "Ordered sequences of areas visited by anonymized panel members, surfaced as place-to-place transition probabilities.",
    supply: "telia-nor",
    algorithm:
      "Trajectories deduplicated per user-day, then aggregated to (origin, destination) pairs with transition counts.",
    perCountryLimitations: [
      "Privacy thresholds suppress small cohorts and rare transitions.",
    ],
    coverageCaveats: "Monthly cadence on Overture places.",
  },
  {
    id: "origin-destination-nor",
    ltm: "origin-destination",
    country: "NOR",
    methodology:
      "Origin-destination matrices computed from anonymized Telia trajectories across an area system.",
    supply: "telia-nor",
    algorithm:
      "Per-user trajectories projected onto the destination area system, deduplicated, and aggregated to (origin, destination, time-bucket) cells.",
    perCountryLimitations: [
      "Granularity bounded by the underlying area system.",
      "Sparse OD pairs are suppressed for privacy.",
    ],
    coverageCaveats:
      "Daily and hourly cadences across all Norwegian area location sets (al4, al3, al2, 250m grid).",
  },
  {
    id: "traffic-nor",
    ltm: "traffic",
    country: "NOR",
    methodology:
      "Aggregated trip counts mapped onto OSM road segments, derived from Telia trajectory observations.",
    supply: "telia-nor",
    algorithm:
      "Trajectories snapped to the OSM way network; per-segment trip-counts published as daily, hourly, and weekly aggregates.",
    perCountryLimitations: [
      "Sparse on minor roads; calibration assumes typical expansion factors.",
    ],
    coverageCaveats: "Daily, hourly, and weekly cadences across all Norwegian OSM ways.",
  },

  // Sweden
  {
    id: "foot-traffic-area-swe",
    ltm: "foot-traffic-vwr",
    country: "SWE",
    methodology:
      "Telia network events mapped to Swedish areas via base-station geometry.",
    supply: "telia-swe",
    algorithm:
      "Network observations projected to areas, deduplicated per user-day, and split into visitor / worker / resident roles.",
    perCountryLimitations: [
      "Aggregation thresholds suppress areas with low visitor counts.",
    ],
    coverageCaveats:
      "Daily, hourly, and monthly cadences across districts (al3), municipalities (al2), 250m grid, and Overture places.",
  },
  {
    id: "visit-length-area-swe",
    ltm: "visit-length-area",
    country: "SWE",
    methodology: "Dwell-time distribution from Telia Sweden network events.",
    supply: "telia-swe",
    algorithm:
      "Dwell-time computation per visit-event, aggregated to medians/percentiles per area.",
    perCountryLimitations: ["Areas with sparse activity have noisier dwell estimates."],
    coverageCaveats: "Daily and monthly cadences across all Swedish area location sets.",
  },
  {
    id: "visitor-demographics-area-swe",
    ltm: "visitor-demographics-area",
    country: "SWE",
    methodology:
      "Demographic profile of visitors, derived from carrier-side demography linked to anonymized panel cohorts.",
    supply: "telia-swe",
    algorithm:
      "Per-area cohort breakdown across age/gender bands; published as fractions, subject to K5 minimum-cohort suppression.",
    perCountryLimitations: [
      "Fractional values, not absolute counts.",
      "Demographic bands are coarse to preserve privacy.",
    ],
    coverageCaveats:
      "Monthly cadence across districts (al3) and Overture places. `resident_demographics_month` available for AL3.",
  },
  {
    id: "trade-area-area-swe",
    ltm: "trade-area-area",
    country: "SWE",
    methodology: "Trade-area inference from home-area-linked panel observations.",
    supply: "telia-swe",
    algorithm:
      "Origins of observed visitors aggregated to the home area system; published as capture-rate per origin.",
    perCountryLimitations: ["Smoothing parameters affect boundary stability."],
    coverageCaveats:
      "Monthly cadence on districts (al3). Cross-system trade areas for Overture places join to AL3 origins.",
  },
  {
    id: "visitor-journey-swe",
    ltm: "visitor-journey",
    country: "SWE",
    methodology:
      "Ordered sequences of areas visited by anonymized panel members, surfaced as place-to-place transition probabilities.",
    supply: "telia-swe",
    algorithm:
      "Trajectories deduplicated per user-day, then aggregated to (origin, destination) pairs with transition counts.",
    perCountryLimitations: ["Privacy thresholds suppress small cohorts."],
    coverageCaveats: "Monthly cadence on Overture places.",
  },
  {
    id: "origin-destination-swe",
    ltm: "origin-destination",
    country: "SWE",
    methodology: "Origin-destination matrices from anonymized Telia Sweden trajectories.",
    supply: "telia-swe",
    algorithm:
      "Per-user trajectories projected onto the destination area system, deduplicated, and aggregated to (origin, destination, time-bucket) cells.",
    perCountryLimitations: [
      "Granularity bounded by the underlying area system.",
      "Sparse OD pairs suppressed for privacy.",
    ],
    coverageCaveats:
      "Daily and hourly cadences across districts (al3), municipalities (al2), and 250m grid.",
  },
  {
    id: "traffic-swe",
    ltm: "traffic",
    country: "SWE",
    methodology:
      "Aggregated trip counts mapped onto OSM road segments, derived from Telia Sweden trajectories.",
    supply: "telia-swe",
    algorithm:
      "Trajectories snapped to the OSM way network; per-segment trip-counts published as daily, hourly, and weekly aggregates.",
    perCountryLimitations: ["Sparse on minor roads."],
    coverageCaveats: "Daily, hourly, and weekly cadences across all Swedish OSM ways.",
  },

  // Finland
  {
    id: "foot-traffic-area-fin",
    ltm: "foot-traffic-vwr",
    country: "FIN",
    methodology: "Telia network events mapped to Finnish areas via base-station geometry.",
    supply: "telia-fin",
    algorithm:
      "Network observations projected to areas, deduplicated per user-day, and split into visitor / worker / resident roles.",
    perCountryLimitations: [
      "Aggregation thresholds suppress areas with low visitor counts.",
    ],
    coverageCaveats:
      "Daily, hourly, and monthly cadences across postal codes (al3), municipalities (al2), 250m grid, and Overture places.",
  },
  {
    id: "visit-length-area-fin",
    ltm: "visit-length-area",
    country: "FIN",
    methodology: "Dwell-time distribution from Telia Finland network events.",
    supply: "telia-fin",
    algorithm:
      "Dwell-time computation per visit-event, aggregated to medians/percentiles per area.",
    perCountryLimitations: ["Areas with sparse activity have noisier dwell estimates."],
    coverageCaveats: "Daily and monthly cadences across all Finnish area location sets.",
  },
  {
    id: "visitor-demographics-area-fin",
    ltm: "visitor-demographics-area",
    country: "FIN",
    methodology:
      "Demographic profile of visitors, derived from carrier-side demography linked to anonymized panel cohorts.",
    supply: "telia-fin",
    algorithm:
      "Per-area cohort breakdown across age/gender bands; published as fractions, subject to K5 minimum-cohort suppression.",
    perCountryLimitations: [
      "Fractional values, not absolute counts.",
      "Demographic bands are coarse to preserve privacy.",
    ],
    coverageCaveats: "Monthly cadence across postal codes (al3) and Overture places.",
  },
  {
    id: "trade-area-area-fin",
    ltm: "trade-area-area",
    country: "FIN",
    methodology: "Trade-area inference from home-area-linked panel observations.",
    supply: "telia-fin",
    algorithm:
      "Origins of observed visitors aggregated to the home area system; published as capture-rate per origin.",
    perCountryLimitations: ["Smoothing parameters affect boundary stability."],
    coverageCaveats:
      "Monthly cadence on postal codes (al3). Cross-system trade areas for Overture places join to AL3 origins.",
  },
  {
    id: "visitor-journey-fin",
    ltm: "visitor-journey",
    country: "FIN",
    methodology:
      "Ordered sequences of areas visited by anonymized panel members, surfaced as place-to-place transition probabilities.",
    supply: "telia-fin",
    algorithm:
      "Trajectories deduplicated per user-day, then aggregated to (origin, destination) pairs with transition counts.",
    perCountryLimitations: ["Privacy thresholds suppress small cohorts."],
    coverageCaveats: "Monthly cadence on Overture places.",
  },
  {
    id: "origin-destination-fin",
    ltm: "origin-destination",
    country: "FIN",
    methodology: "Origin-destination matrices from anonymized Telia Finland trajectories.",
    supply: "telia-fin",
    algorithm:
      "Per-user trajectories projected onto the destination area system, deduplicated, and aggregated to (origin, destination, time-bucket) cells.",
    perCountryLimitations: [
      "Granularity bounded by the underlying area system.",
      "Sparse OD pairs suppressed for privacy.",
    ],
    coverageCaveats:
      "Daily and hourly cadences across postal codes (al3), municipalities (al2), and 250m grid.",
  },
  {
    id: "traffic-fin",
    ltm: "traffic",
    country: "FIN",
    methodology:
      "Aggregated trip counts mapped onto OSM road segments, derived from Telia Finland trajectories.",
    supply: "telia-fin",
    algorithm:
      "Trajectories snapped to the OSM way network; per-segment trip-counts published as daily, hourly, and weekly aggregates.",
    perCountryLimitations: ["Sparse on minor roads."],
    coverageCaveats: "Daily, hourly, and weekly cadences across all Finnish OSM ways.",
  },
]

export const locationSets: LocationSet[] = [
  {
    id: "safegraph_places_usa",
    name: "Safegraph Places (USA)",
    shortName: "Safegraph Places",
    locationType: "place",
    country: "USA",
    source: "Safegraph",
    description:
      "The Safegraph point-of-interest set covers the United States with several million places including retail, dining, services, and public venues.",
    coverage: "Continental USA + Hawaii + Alaska. Continuously updated.",
  },
  {
    id: "overture_est",
    name: "Overture Places (Estonia)",
    shortName: "Overture Places",
    locationType: "place",
    country: "EST",
    source: "Overture Maps Foundation",
    description:
      "Overture's open Places dataset covers Estonia with cross-validated points-of-interest.",
    coverage: "Estonia, all administrative levels.",
  },
  {
    id: "neighborhood_est",
    name: "Estonian Neighborhoods (al3)",
    shortName: "Neighborhoods (al3)",
    locationType: "area",
    country: "EST",
    source: "Estonian admin levels (al3)",
    description: "Statistical neighborhood units used by Statistics Estonia.",
    coverage: "All Estonian neighborhoods.",
  },
  {
    id: "region_est",
    name: "Estonian Regions (al2)",
    shortName: "Regions (al2)",
    locationType: "area",
    country: "EST",
    source: "Estonian admin levels (al2)",
    description: "Estonian administrative regions (maakond).",
    coverage: "15 regions.",
  },
  {
    id: "grid_est",
    name: "Estonian 250m Grid",
    shortName: "250m Grid",
    locationType: "area",
    country: "EST",
    source: "Pine59 standard 250m grid",
    description:
      "Uniform 250m grid covering Estonia. Useful for fine-grained spatial analyses where administrative boundaries are too coarse.",
    coverage: "All Estonian land area.",
  },
  {
    id: "osm_est",
    name: "Estonian OSM Ways",
    shortName: "OSM Ways",
    locationType: "way",
    country: "EST",
    source: "OpenStreetMap",
    description:
      "OpenStreetMap road and path segments across Estonia. Used as the way set for traffic and ODM-way metrics.",
    coverage: "The classified Estonian through-road network in OSM: motorway, trunk, primary, secondary, and tertiary, plus their link roads.",
  },

  // Norway
  {
    id: "overture_nor",
    name: "Overture Places (Norway)",
    shortName: "Overture Places",
    locationType: "place",
    country: "NOR",
    source: "Overture Maps Foundation",
    description:
      "Overture's open Places dataset covers Norway with cross-validated points-of-interest.",
    coverage: "Norway, all administrative levels.",
  },
  {
    id: "neighborhood_al4_nor",
    name: "Norwegian Basic Statistical Units (al4)",
    shortName: "Basic Statistical Units (al4)",
    locationType: "area",
    country: "NOR",
    source: "Statistics Norway (SSB) — grunnkrets / al4",
    description:
      "The finest administrative unit published by Statistics Norway. Used for fine-grained neighborhood analysis where AL3 is too coarse.",
    coverage: "All Norwegian land area.",
  },
  {
    id: "neighborhood_al3_nor",
    name: "Norwegian Statistical Tracts (al3)",
    shortName: "Statistical Tracts (al3)",
    locationType: "area",
    country: "NOR",
    source: "Statistics Norway (SSB) — delområde / al3",
    description: "Statistical tracts (delområde) used by Statistics Norway.",
    coverage: "All Norwegian municipalities with published tracts.",
  },
  {
    id: "region_nor",
    name: "Norwegian Municipalities (al2)",
    shortName: "Municipalities (al2)",
    locationType: "area",
    country: "NOR",
    source: "Statistics Norway (SSB) — kommune / al2",
    description: "Norwegian municipalities (kommune).",
    coverage: "All 357 Norwegian municipalities post-2024 reform.",
  },
  {
    id: "grid_nor",
    name: "Norwegian 250m Grid",
    shortName: "250m Grid",
    locationType: "area",
    country: "NOR",
    source: "Pine59 standard 250m grid",
    description:
      "Uniform 250m grid covering Norway. Useful for fine-grained spatial analyses where administrative boundaries are too coarse.",
    coverage: "All Norwegian mainland + Svalbard.",
  },
  {
    id: "osm_nor",
    name: "Norwegian OSM Ways",
    shortName: "OSM Ways",
    locationType: "way",
    country: "NOR",
    source: "OpenStreetMap",
    description:
      "OpenStreetMap road and path segments across Norway. Used as the way set for traffic and ODM-way metrics.",
    coverage: "The classified Norwegian through-road network in OSM: motorway, trunk, primary, secondary, and tertiary, plus their link roads.",
  },

  // Sweden
  {
    id: "overture_swe",
    name: "Overture Places (Sweden)",
    shortName: "Overture Places",
    locationType: "place",
    country: "SWE",
    source: "Overture Maps Foundation",
    description:
      "Overture's open Places dataset covers Sweden with cross-validated points-of-interest.",
    coverage: "Sweden, all administrative levels.",
  },
  {
    id: "neighborhood_al3_swe",
    name: "Swedish Districts (al3)",
    shortName: "Districts (al3)",
    locationType: "area",
    country: "SWE",
    source: "Statistics Sweden (SCB) — distrikt / al3",
    description: "Swedish parish districts (distrikt) used by Statistics Sweden.",
    coverage: "All Swedish municipalities.",
  },
  {
    id: "region_swe",
    name: "Swedish Municipalities (al2)",
    shortName: "Municipalities (al2)",
    locationType: "area",
    country: "SWE",
    source: "Statistics Sweden (SCB) — kommun / al2",
    description: "Swedish municipalities (kommun).",
    coverage: "All 290 Swedish municipalities.",
  },
  {
    id: "grid_swe",
    name: "Swedish 250m Grid",
    shortName: "250m Grid",
    locationType: "area",
    country: "SWE",
    source: "Pine59 standard 250m grid",
    description:
      "Uniform 250m grid covering Sweden. Useful for fine-grained spatial analyses where administrative boundaries are too coarse.",
    coverage: "All Swedish land area.",
  },
  {
    id: "osm_swe",
    name: "Swedish OSM Ways",
    shortName: "OSM Ways",
    locationType: "way",
    country: "SWE",
    source: "OpenStreetMap",
    description:
      "OpenStreetMap road and path segments across Sweden. Used as the way set for traffic and ODM-way metrics.",
    coverage: "The classified Swedish through-road network in OSM: motorway, trunk, primary, secondary, and tertiary, plus their link roads.",
  },

  // Finland
  {
    id: "overture_fin",
    name: "Overture Places (Finland)",
    shortName: "Overture Places",
    locationType: "place",
    country: "FIN",
    source: "Overture Maps Foundation",
    description:
      "Overture's open Places dataset covers Finland with cross-validated points-of-interest.",
    coverage: "Finland, all administrative levels.",
  },
  {
    id: "neighborhood_al3_fin",
    name: "Finnish Postal Codes (al3)",
    shortName: "Postal Codes (al3)",
    locationType: "area",
    country: "FIN",
    source: "Statistics Finland — postinumero / al3",
    description: "Finnish postal-code areas, used as the al3 administrative tier.",
    coverage: "All Finnish municipalities with published postal codes.",
  },
  {
    id: "region_fin",
    name: "Finnish Municipalities (al2)",
    shortName: "Municipalities (al2)",
    locationType: "area",
    country: "FIN",
    source: "Statistics Finland — kunta / al2",
    description: "Finnish municipalities (kunta).",
    coverage: "All 309 Finnish municipalities.",
  },
  {
    id: "grid_fin",
    name: "Finnish 250m Grid",
    shortName: "250m Grid",
    locationType: "area",
    country: "FIN",
    source: "Pine59 standard 250m grid",
    description:
      "Uniform 250m grid covering Finland. Useful for fine-grained spatial analyses where administrative boundaries are too coarse.",
    coverage: "All Finnish land area.",
  },
  {
    id: "osm_fin",
    name: "Finnish OSM Ways",
    shortName: "OSM Ways",
    locationType: "way",
    country: "FIN",
    source: "OpenStreetMap",
    description:
      "OpenStreetMap road and path segments across Finland. Used as the way set for traffic and ODM-way metrics.",
    coverage: "The classified Finnish through-road network in OSM: motorway, trunk, primary, secondary, and tertiary, plus their link roads.",
  },
]

export const supplySources: SupplySource[] = [
  {
    id: "gps-usa",
    name: "GPS — USA",
    shortName: "GPS",
    country: "USA",
    signal: "gps",
    description:
      "Panel-based GPS observations from a representative sample of US mobile users. Used as the primary signal for USA foot-traffic and movement metrics.",
    body: `## From signal to visits

GPS-based Foot Traffic is produced in two steps: detecting when devices in the panel stay in one place, and estimating from those stays how many people visit each place.

### Detecting dwells

Dwells are periods when a device stays in the same place. They are detected as part of the aggregation described below: time spent moving between places, for example driving or walking, is left out, and only stationary periods count. A dwell belongs to the local day on which it starts, and only aggregated dwell observations reach Pine59.

### Estimating visitors

For each place, our model uses the dwells on the place's polygon together with context about the place, such as its brand, its type of location and its surroundings. It has learned from a wide range of ground-truth visitation data how dwells relate to real visitor numbers for different kinds of places, and uses this to estimate the number of unique visitors to the place each day. Weekly and monthly figures add up the daily counts, so a person who visits on several days is counted once for each of those days. How the panel is scaled to the full population is described under [Extrapolation](/docs/datasets/usa/methodology/extrapolation).

## Aggregation starts on the device

The panel signal is aggregated **client-side, before it ever reaches Pine59**. What we receive are aggregated observations, not raw location traces: no names, no advertising identifiers, no device-level movement histories. There is no personal data in our pipeline to leak, sell, or subpoena, because it never crosses our boundary in the first place.

Every published statistic is then further protected by the methodology below (extrapolation to population estimates, minimum-cohort suppression, rounding), so no output can be traced back to any individual panel member.

## Why this matters in restricted states

Several US states have passed legislation restricting the sale of individual location data. Because the Pine59 pipeline never handles individual-level location data, with the aggregation happening at the source, we can continue to produce and sell aggregated visitation statistics in markets where raw location-data products cannot operate. The signal is privacy-safe by construction, not by after-the-fact scrubbing.

## Stability over time

The panel of devices that provides the underlying signal naturally changes over time, for a variety of reasons. To keep estimates stable over time, it is essential to compensate for these changes, so that trends reflect real-world trends rather than changes in supply. Our modeling includes several techniques for ensuring panel stability:

- **Market share.** [Extrapolation](/docs/datasets/usa/methodology/extrapolation) is updated dynamically to reflect the panel's real market share at any given time, so a growing or shrinking panel does not change visit levels.
- **Signal density.** Some sources only record a device's whereabouts at certain times of day, and this varies between sources and over time. We measure how much activity is recorded per device each day and correct for changes in it, so a change in reporting frequency does not read as a change in visits.
- **Real patterns pass through.** The corrections are measured on ordinary weekdays, so real differences between weekdays, weekends, holidays and seasons stay in the data.`,
    availability: "Continuous since 2019.",
    latestData: "Updated daily; processing lag ~5–6 days.",
  },
  {
    id: "telia-est",
    name: "Telia — Estonia",
    shortName: "Telia",
    country: "EST",
    signal: "telco",
    description:
      "Telco network events from Telia Estonia. Used as the primary signal for Estonian foot-traffic and area metrics, with GPS-based calibration.",
    availability: "Continuous since 2019.",
    latestData: "Updated daily; processing lag ~5–6 days.",
  },
  {
    id: "telia-nor",
    name: "Telia — Norway",
    shortName: "Telia",
    country: "NOR",
    signal: "telco",
    description:
      "Telco network events from Telia Norway. Used as the primary signal for Norwegian foot-traffic, ODM, and traffic metrics. GPS-calibrated where panel data is available.",
    availability: "Continuous since 2019.",
    latestData: "Updated daily; processing lag ~5–6 days.",
  },
  {
    id: "telia-swe",
    name: "Telia — Sweden",
    shortName: "Telia",
    country: "SWE",
    signal: "telco",
    description:
      "Telco network events from Telia Sweden. Used as the primary signal for Swedish foot-traffic, ODM, and traffic metrics.",
    availability: "Continuous since 2019.",
    latestData: "Updated daily; processing lag ~5–6 days.",
  },
  {
    id: "telia-fin",
    name: "Telia — Finland",
    shortName: "Telia",
    country: "FIN",
    signal: "telco",
    description:
      "Telco network events from Telia Finland. Used as the primary signal for Finnish foot-traffic, ODM, and traffic metrics.",
    availability: "Continuous since 2020.",
    latestData: "Updated daily; processing lag ~5–6 days.",
  },
]
